import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { AlertTriangle, Check, Image as ImageIcon, X } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { platformGroups, platformFormatCombos, PlatformFormatCombo } from './platformDefinitions';
import { SpinnerIcon } from './Spinner';

interface ReformatWithMarginsConfig {
  combos: PlatformFormatCombo[];
  productRefs?: File[];
}

interface ReformatPanelProps {
  onReformat: (config: ReformatWithMarginsConfig) => void;
  onHoverCombo: (combo: PlatformFormatCombo | null) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  hasImage: boolean;
}

const getAspectRatioStyle = (ratio: string) => {
  const [w, h] = ratio.split(':').map(Number);
  return { aspectRatio: `${w}/${h}` };
};

const ReformatPanel: React.FC<ReformatPanelProps> = ({ onReformat, onHoverCombo, isLoading, isSubmitting, hasImage }) => {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [productRefs, setProductRefs] = useState<{ file: File; preview: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { useBrandGuidelines, brandAssets } = useSettings();

  const templateAssets = useMemo(
    () => brandAssets.filter((a) => a.type === "template"),
    [brandAssets]
  );

  const hasAnyLabeledTemplate = useMemo(
    () => templateAssets.some((a) => {
      const m = a.metadata || {};
      return (m.platforms?.length ?? 0) > 0 && (m.aspect_ratios?.length ?? 0) > 0;
    }),
    [templateAssets]
  );

  const comboHasMatchingTemplate = useCallback(
    (platformLabel: string, aspectRatio: string): boolean => {
      if (!useBrandGuidelines || templateAssets.length === 0) return true;
      if (!hasAnyLabeledTemplate) return true; // all unlabeled → all match
      return templateAssets.some((a) => {
        const m = a.metadata || {};
        const platforms: string[] = m.platforms || [];
        const ratios: string[] = m.aspect_ratios || [];
        return platforms.includes(platformLabel) && ratios.includes(aspectRatio);
      });
    },
    [useBrandGuidelines, templateAssets, hasAnyLabeledTemplate]
  );

  const handleProductRefUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newRefs = Array.from(files).slice(0, 5 - productRefs.length).map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
    }));
    setProductRefs((prev) => [...prev, ...newRefs]);
    e.target.value = '';
  }, [productRefs.length]);

  const handleRemoveRef = useCallback((index: number) => {
    setProductRefs((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Keep a ref to current productRefs so the unmount cleanup reads the latest value
  const productRefsRef = useRef<{ file: File; preview: string }[]>([]);
  productRefsRef.current = productRefs;
  useEffect(() => {
    return () => {
      productRefsRef.current.forEach((r) => URL.revokeObjectURL(r.preview));
    };
  }, []);

  const combosByPlatform = useMemo(() => {
    const map = new Map<string, PlatformFormatCombo[]>();
    platformFormatCombos.forEach((c) => {
      if (!map.has(c.platformLabel)) map.set(c.platformLabel, []);
      map.get(c.platformLabel)!.push(c);
    });
    return map;
  }, []);

  const toggleCombo = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = (platformLabel: string) => {
    const combos = combosByPlatform.get(platformLabel) ?? [];
    const allSelected = combos.every((c) => selectedKeys.has(c.key));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        combos.forEach((c) => next.delete(c.key));
      } else {
        combos.forEach((c) => next.add(c.key));
      }
      return next;
    });
  };

  const handleReformat = () => {
    const combos = platformFormatCombos.filter((c) => selectedKeys.has(c.key));
    if (combos.length > 0) onReformat({ combos, productRefs: productRefs.map((r) => r.file) });
  };

  const totalSelected = selectedKeys.size;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto flex flex-col gap-6 pb-2">
      {platformGroups.map(({ platformLabel, logos }) => {
        const combos = combosByPlatform.get(platformLabel) ?? [];
        if (combos.length === 0) return null;

        return (
          <div key={platformLabel}>
            {/* Platform header */}
            <div className="flex items-center gap-2 mb-2.5 px-0.5">
              <div className="flex items-center gap-1">
                {logos.map((logo, i) => (
                  <img key={i} src={logo} alt={platformLabel} className="w-5 h-5 object-contain rounded-md" />
                ))}
              </div>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex-1">{platformLabel}</span>
              <button
                onClick={() => toggleSelectAll(platformLabel)}
                className="text-[10px] font-medium text-gray-500 hover:text-blue-400 transition-colors"
              >
                {(combosByPlatform.get(platformLabel) ?? []).every((c) => selectedKeys.has(c.key)) ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            {/* Format cards */}
            <div className="grid grid-cols-2 gap-2.5">
              {combos.map((combo) => {
                const isSelected = selectedKeys.has(combo.key);
                return (
                  <button
                    key={combo.key}
                    onClick={() => toggleCombo(combo.key)}
                    onMouseEnter={() => onHoverCombo(combo)}
                    onMouseLeave={() => onHoverCombo(null)}
                    disabled={isLoading}
                    className={`relative flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200
                      ${isSelected
                        ? 'bg-blue-500/10 border-blue-500 shadow-lg shadow-blue-500/10'
                        : 'bg-gray-800/40 border-gray-700 hover:border-gray-500 hover:bg-gray-700/40'
                      }`}
                  >
                    <div
                      className={`w-8 mb-2 rounded-sm border-2 transition-colors ${isSelected ? 'border-blue-400 bg-blue-400/20' : 'border-gray-500 bg-gray-500/10'}`}
                      style={getAspectRatioStyle(combo.aspectRatio)}
                    />
                    <span className={`text-[11px] font-semibold text-center leading-tight ${isSelected ? 'text-blue-200' : 'text-gray-300'}`}>
                      {combo.label}
                    </span>
                    <span className={`text-[10px] mt-0.5 ${isSelected ? 'text-blue-400/70' : 'text-gray-500'}`}>
                      {combo.targetWidth ? `${combo.targetWidth}x${combo.targetHeight}` : combo.aspectRatio}
                    </span>
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 bg-blue-500 rounded-full p-0.5">
                        <Check className="w-2 h-2 text-white" />
                      </div>
                    )}
                    {useBrandGuidelines && !comboHasMatchingTemplate(combo.platformLabel, combo.aspectRatio) && (
                      <div className="absolute top-1.5 left-1.5" title="No matching template for this format">
                        <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      </div>

      {/* Product reference images */}
      <div className="pt-3 border-t border-gray-700/50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Product References</span>
          <span className="text-[10px] text-gray-500">{productRefs.length}/5</span>
        </div>
        {productRefs.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-2">
            {productRefs.map((ref, i) => (
              <div key={i} className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-600">
                <img src={ref.preview} alt={`Product ref ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => handleRemoveRef(i)}
                  className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-red-500/90 hover:bg-red-500 text-white"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        {productRefs.length < 5 && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="w-full py-2 rounded-lg border border-dashed border-gray-600 hover:border-gray-400 text-xs text-gray-400 hover:text-gray-300 transition-colors flex items-center justify-center gap-1.5"
          >
            <ImageIcon className="w-3.5 h-3.5" />
            Add product photos
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={handleProductRefUpload}
        />
        <p className="text-[10px] text-gray-500 mt-1">Upload clean product shots so the AI reproduces the product accurately.</p>
      </div>

      {/* Generate button — pinned at bottom */}
      <div className="pt-3">
        <button
          onClick={handleReformat}
          disabled={isLoading || isSubmitting || !hasImage || totalSelected === 0}
          className="w-full bg-gradient-to-br from-blue-600 to-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 ease-in-out shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-px active:scale-95 disabled:from-blue-800 disabled:to-blue-700 disabled:shadow-none disabled:cursor-not-allowed disabled:transform-none"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <SpinnerIcon />
              Submitting...
            </span>
          ) : isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <SpinnerIcon />
              Reformatting...
            </span>
          ) : (
            totalSelected > 0
              ? `Generate ${totalSelected} Format${totalSelected > 1 ? 's' : ''}`
              : 'Select Formats'
          )}
        </button>
      </div>
    </div>
  );
};

export default ReformatPanel;
